import React, { useState } from 'react';
import { InvestmentItemCompareData, ReportItemCompareData } from '../type/AssetReport.type';
import { numberWithCommas } from '../../../common/util/NumberUtils';

export interface CompareAssetCardAccordionData {
  totalChangeAmount: number;
  savingChangeAmount: number;
  investmentChangeAmount: number;
  investmentChangePrincipalAmount: number;
  investmentChangeProfitAmount: number;
  etcChangeAmount: number;
  compareSavingItemList: ReportItemCompareData[];
  compareInvestmentItemList: InvestmentItemCompareData[];
  compareEtcItemList: ReportItemCompareData[];
}

const CompareAssetCardAccordion = ({
  totalChangeAmount,
  savingChangeAmount,
  investmentChangeAmount,
  investmentChangePrincipalAmount,
  investmentChangeProfitAmount,
  etcChangeAmount,
  compareSavingItemList,
  compareInvestmentItemList,
  compareEtcItemList,
}: CompareAssetCardAccordionData) => {
  const [accordionState, setAccordionState] = useState({
    savings: false,
    investment: false,
    others: false,
  });

  const toggleAccordion = (section: 'savings' | 'investment' | 'others') => {
    setAccordionState((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  return (
    <div className="space-y-2 mt-4">
      <div className="border border-gray-200 rounded-md overflow-hidden">
        <div className="w-full flex justify-between items-center p-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors !rounded-button whitespace-nowrap cursor-pointer">
          <span className="font-medium">총 증감액</span>
          <span className="flex items-center">
            <span className={`text-${totalChangeAmount >= 0 ? 'green' : 'red'}-600 font-medium mr-6`}>
              {totalChangeAmount > 0 ? '+' : ''}
              {numberWithCommas(totalChangeAmount)} 원
            </span>
          </span>
        </div>
      </div>
      <div className="border border-gray-200 rounded-md overflow-hidden">
        <button
          onClick={() => toggleAccordion('savings')}
          className="w-full flex justify-between items-center p-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors !rounded-button whitespace-nowrap cursor-pointer"
        >
          <span className="font-medium">저축 증감액</span>
          <span className="flex items-center">
            <span className={`text-${savingChangeAmount >= 0 ? 'green' : 'red'}-600 font-medium mr-2`}>
              {savingChangeAmount > 0 ? '+' : ''}
              {numberWithCommas(savingChangeAmount)} 원
            </span>
            <i className={`fas ${accordionState.savings ? 'fa-chevron-up' : 'fa-chevron-down'} text-gray-500`}></i>
          </span>
        </button>
        {accordionState.savings && (
          <div className="p-3 bg-white">
            <table className="w-full text-sm">
              <tbody>
                {compareSavingItemList
                  .filter((compareSavingItem) => compareSavingItem.changeAmount !== 0)
                  .map((compareSavingItem, index) => (
                    <tr className={index < compareSavingItemList.length ? 'border-b' : ''}>
                      <td className="py-2">{compareSavingItem.item?.name ?? compareSavingItem.prevItem?.name}</td>
                      <td
                        className={`whitespace-nowrap w-30 py-2 text-right text-${compareSavingItem.changeAmount > 0 ? 'green' : 'red'}-600`}
                      >
                        {compareSavingItem.changeAmount > 0 ? '+' : ''}
                        {numberWithCommas(compareSavingItem.changeAmount)} 원
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* 투자 증감액 */}
      <div className="border border-gray-200 rounded-md overflow-hidden">
        <button
          onClick={() => toggleAccordion('investment')}
          className="w-full flex justify-between items-center p-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors !rounded-button whitespace-nowrap cursor-pointer"
        >
          <div>
            <span className="font-medium">
              투자 증감액
              <br className="hidden [@media(max-width:500px)]:block lg:block xl:hidden" />
              (원금, 수익)
            </span>
          </div>
          <div className="hidden [@media(max-width:500px)]:flex lg:flex xl:hidden items-center text-right">
            <span className={`text-${investmentChangeAmount >= 0 ? 'green' : 'red'}-600 font-medium mr-2`}>
              <span>
                {investmentChangeAmount > 0 ? '+' : ''}
                {numberWithCommas(investmentChangeAmount)} 원
              </span>
              <br className="hidden [@media(max-width:500px)]:block lg:block xl:hidden" />(
              <span className={`text-${investmentChangePrincipalAmount >= 0 ? 'green' : 'red'}-600 font-medium mr-2`}>
                {investmentChangePrincipalAmount > 0 ? '+' : ''}
                {numberWithCommas(investmentChangePrincipalAmount)},
              </span>
              <span className={`text-${investmentChangeProfitAmount >= 0 ? 'green' : 'red'}-600 font-medium`}>
                {investmentChangeProfitAmount > 0 ? '+' : ''}
                {numberWithCommas(investmentChangeProfitAmount)}
              </span>
              )
            </span>
            <i className={`fas ${accordionState.savings ? 'fa-chevron-up' : 'fa-chevron-down'} text-gray-500`}></i>
          </div>
          <div className="block [@media(max-width:500px)]:hidden lg:hidden xl:block items-center">
            <span className={`text-${investmentChangeAmount >= 0 ? 'green' : 'red'}-600 font-medium mr-2`}>
              {investmentChangeAmount > 0 ? '+' : ''}
              {numberWithCommas(investmentChangeAmount)}(
              <span className={`text-${investmentChangePrincipalAmount >= 0 ? 'green' : 'red'}-600 font-medium mr-2`}>
                {investmentChangePrincipalAmount > 0 ? '+' : ''}
                {numberWithCommas(investmentChangePrincipalAmount)},
              </span>
              <span className={`text-${investmentChangeProfitAmount >= 0 ? 'green' : 'red'}-600 font-medium`}>
                {investmentChangeProfitAmount > 0 ? '+' : ''}
                {numberWithCommas(investmentChangeProfitAmount)}
              </span>
              ) 원
            </span>
            <i className={`fas ${accordionState.savings ? 'fa-chevron-up' : 'fa-chevron-down'} text-gray-500`}></i>
          </div>
        </button>
        {accordionState.investment && (
          <div className="p-3 bg-white">
            <table className="w-full text-sm">
              <tbody>
                {compareInvestmentItemList
                  .filter((compareInvestmentItem) => compareInvestmentItem.changeAmount !== 0)
                  .map((compareInvestmentItem, index) => (
                    <tr className={index < compareInvestmentItemList.length ? 'border-b' : ''}>
                      <td className="py-2">
                        {compareInvestmentItem.item?.name ?? compareInvestmentItem.prevItem?.name}
                      </td>
                      <td
                        className={`whitespace-nowrap py-2 text-right text-${compareInvestmentItem.changeAmount > 0 ? 'green' : 'red'}-600`}
                      >
                        {compareInvestmentItem.changeAmount > 0 ? '+' : ''}
                        {numberWithCommas(compareInvestmentItem.changeAmount) + ' '}
                        <span className="hidden [@media(max-width:500px)]:inline lg:inline xl:hidden">원</span>
                        <br className="hidden [@media(max-width:500px)]:block lg:block xl:hidden" />(
                        <span
                          className={`text-${compareInvestmentItem.changePrincipalAmount > 0 ? 'green' : 'red'}-600`}
                        >
                          {compareInvestmentItem.changePrincipalAmount > 0 ? '+' : ''}
                          {numberWithCommas(compareInvestmentItem.changePrincipalAmount)},{' '}
                        </span>
                        <span
                          className={`text-${compareInvestmentItem.changeAmount - compareInvestmentItem.changePrincipalAmount > 0 ? 'green' : 'red'}-600`}
                        >
                          {compareInvestmentItem.changeAmount - compareInvestmentItem.changePrincipalAmount > 0
                            ? '+'
                            : ''}
                          {numberWithCommas(
                            compareInvestmentItem.changeAmount - compareInvestmentItem.changePrincipalAmount,
                          )}
                        </span>
                        )<span className="inline [@media(max-width:500px)]:hidden lg:hidden xl:inline"> 원</span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {/* 기타 증감액 */}
      <div className="border border-gray-200 rounded-md overflow-hidden">
        <button
          onClick={() => toggleAccordion('others')}
          className="w-full flex justify-between items-center p-3 text-left bg-gray-50 hover:bg-gray-100 transition-colors !rounded-button whitespace-nowrap cursor-pointer"
        >
          <span className="font-medium">기타 증감액</span>
          <span className="flex items-center">
            <span className={`text-${etcChangeAmount >= 0 ? 'green' : 'red'}-600 font-medium mr-2`}>
              {etcChangeAmount > 0 ? '+' : ''}
              {numberWithCommas(etcChangeAmount)} 원
            </span>
            <i className={`fas ${accordionState.others ? 'fa-chevron-up' : 'fa-chevron-down'} text-gray-500`}></i>
          </span>
        </button>
        {accordionState.others && (
          <div className="p-3 bg-white">
            <table className="w-full text-sm">
              <tbody>
                {compareEtcItemList
                  .filter((compareEtcItem) => compareEtcItem.changeAmount !== 0)
                  .map((compareEtcItem, index) => (
                    <tr className={index < compareSavingItemList.length ? 'border-b' : ''}>
                      <td className="py-2">{compareEtcItem.item?.name ?? compareEtcItem.prevItem?.name}</td>
                      <td
                        className={`whitespace-nowrap w-30 py-2 text-right text-${compareEtcItem.changeAmount > 0 ? 'green' : 'red'}-600`}
                      >
                        {compareEtcItem.changeAmount > 0 ? '+' : ''}
                        {numberWithCommas(compareEtcItem.changeAmount)} 원
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default CompareAssetCardAccordion;
